import { useAuth } from "@hooks/useAuth";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import useGzLog from "@hooks/useGzLog";
import { useInfiniteQuery } from "@tanstack/react-query";
import generateAssetUrl from "@utils/generateAssetUrl";
import { imageToBase64 } from "@utils/imageToBase64";
import { serverEndpoints } from "@utils/serverEndpoints";
import { authClient } from "@utils/authClient";
import { getPagination, selectData, softDelete, supabase } from "@utils/serverClient";
import { useMemo, useState } from "react";
import { Asset, AssetWithUrl } from "./types";

export const useAssets = () => {
  const paginate = true
  const { log } = useGzLog();

  const [pageSize, setPageSize] = useState(30);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);

  const { organization } = useAuth();

  const queryKey = ["assets", organization?.id, searchQuery, paginate]

  const { mutate: updateAsset, isPending: isUpdatingAsset } = useDataMutation({
    mutationFn: async ({ name, tags, id }: Pick<Asset, 'name' | 'tags' | 'id'>) => {
      const { error } = await supabase
        .from("asset")
        .update({
          name,
          tags,
        })
        .eq("id", id);

      if (error) {
        throw new Error(error.message);
      }
      return null;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update asset",
    optimisticUpdateFn: (oldData: Asset[] | undefined, newAsset) => {
      if (!oldData) return oldData;
      return oldData.map((asset) =>
        asset.id === newAsset.id ? { ...asset, ...newAsset } : asset
      );
    },
  });

  const { mutate: uploadAsset, isPending: isUploading } = useDataMutation({
    mutationFn: async (acceptedFiles: File[]) => {
      if (!organization?.id) return;
      if (acceptedFiles.length === 0) return;

      for (let i = 0; i < acceptedFiles.length; i++) {
        const serverUrl = serverEndpoints.uploadAsset;
        const acceptedFile = acceptedFiles[i];
        const bitmap = await createImageBitmap(acceptedFile);
        const dimension = `${bitmap.width}x${bitmap.height}`;

        const fileExtension = acceptedFile.name.split(".")[1];
        const fileName = acceptedFile.name.split(".")[0];
        const metadata = {
          name: fileName,
          dimension: dimension,
          extension: fileExtension,
          org_id: organization.id,
        };

        const accessToken = await authClient.getAccessToken();
        const base64Data = await imageToBase64(acceptedFile);
        const response = await fetch(serverUrl, {
          method: "POST",
          body: JSON.stringify({
            file: base64Data,
            metadata
          }),
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          credentials: "include",
        });

        if (!response.ok) throw new Error("Error uploading file");

        const newRow = await response.json() as Asset;

        if (!newRow.id) throw new Error("Error uploading file");

        return newRow
      }
    },
    queryKeyToInvalidate: queryKey,
    optimisticUpdateFn: (oldData: Asset[] | undefined, files) => {
      return [
        ...files.map<AssetWithUrl>(f => ({
          created_at: new Date().toISOString(),
          deleted: false,
          dimension: null,
          extension: f.name.split(".").pop() ?? "",
          id: 'temp-' + Date.now(),
          name: f.name,
          org_id: organization?.id ?? null,
          tags: [],
          updated_at: null,
          url: URL.createObjectURL(f),
        })),
        ...(oldData ?? [])
      ];
    },
    options: {
      onSuccess: (_, acceptedFiles) => {
        log({
          eventtype: "AssetUploaded",
          name: "AssetUploaded",
          payload: {
            fileNames: acceptedFiles.map(f => f.name),
            fileCount: 1,
          },
        });
      },
      onError: (error, acceptedFiles) => {
        log({
          eventtype: "AssetUploadFailed",
          name: "AssetUploadFailed",
          payload: {
            fileNames: acceptedFiles.map(f => f.name),
            fileCount: 1,
            errorMessage: error.message,
          },
        });
      }
    }
  });

  const {
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    data,
    status: assetsFetchStatus,
    refetch: getAssets,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      if (pageParam === undefined) return;

      const { from, to } = getPagination(pageParam, pageSize);

      const orgId = organization?.id ?? null;

      if (!orgId) return;
      const { data, error } = paginate
        ? searchQuery
          ? await selectData({
            table: "asset",
            columns: `*`,
          })
            .eq("org_id", orgId)
            .order("created_at", { ascending: false })
            .range(from, to)
            .textSearch("name", searchQuery)
          : await selectData({
            table: "asset",
            columns: `*`,
          })
            .eq("org_id", orgId)
            .order("created_at", { ascending: false })
            .range(from, to)
        : await selectData({
          table: "asset",
          columns: `*`,
        })
          .eq("org_id", orgId)
          .order("created_at", { ascending: false });

      if (error || !data) return;
      return data.map((e) => ({
        ...e,
        url: generateAssetUrl(e.id, 75),
      }));
    },
    getNextPageParam: (_lastGroup, groups) =>
      (_lastGroup ?? [])?.length < pageSize ? undefined : groups.length,
    initialPageParam: 0,
  });

  const { mutate: deleteAsset } = useDataMutation({
    mutationFn: async (assetId: string) => {
      const { error, data } = await softDelete({
        table: "asset",
      }).match({ id: assetId });
      if (error) throw error;
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to delete asset",
    optimisticUpdateFn: (oldData: Asset[] | undefined, assetId) => {
      if (!oldData) return oldData;
      return oldData.filter((asset) => asset.id !== assetId);
    },
    options: {
      onSuccess: (_, assetId) => {
        log({
          eventtype: "AssetDeleted",
          name: "AssetDeleted",
          payload: {
            assetId,
          },
        });
      },
      onError: (error, assetId) => {
        console.log(error)
        log({
          eventtype: "AssetDeleteFailed",
          name: "AssetDeleteFailed",
          payload: {
            assetId,
            errorMessage: error.message,
          },
        });
      }
    }
  })

  const assets = useMemo(() => data?.pages.flatMap((e) => e!) ?? [], [data])

  return {
    assets,
    assetsFetchStatus,
    getAssets,
    updateAsset,
    isUpdatingAsset,
    deleteAsset,
    isUploading,
    uploadAsset,
    hasNextPage,
    pageSize,
    setPageSize,
    searchQuery,
    setSearchQuery,
    isFetchingNextPage,
    fetchNextPage,
  };
};
